import { GRID_TYPE } from '@eventbrite/eds-media-card-content';
import {
    CONSUMER_CARD_DEFAULTS,
    EventCard,
    FormattedEvent,
    parseFollowSignal,
} from '@eventbrite/event-renderer';
import classNames from 'classnames';
import reduce from 'lodash/reduce';
import take from 'lodash/take';
import React, { useEffect } from 'react';
import { GA_HOME_FEED_CATEGORY } from '../../../../constants/analytics';
import { getLocationInfo } from '../../../../utils/venue';
import { CategoryTiles } from '../../containers/CategoryTiles';
import { OnboardingEntry } from '../OnboardingEntry';
import './ConsumerOnboardingCard.scss';

const _insertCategories = ({
    cards,
    isInOddLayout,
}: {
    cards: React.ReactElement[];
    isInOddLayout?: boolean;
}): React.ReactElement[] => {
    const insertIndex = isInOddLayout ? 3 : 4;
    const startChunk = cards.slice(0, insertIndex);
    const endChunk = cards.slice(insertIndex);

    return [...startChunk, <CategoryTiles key="test" />, ...endChunk];
};

const _insertOnboardingCard = ({
    cardsComponent,
    eventCardContainerClassName,
    isAuthenticated,
    isInOddLayout,
    onClickOnBoardingCard,
}: {
    cardsComponent: React.ReactElement[];
    eventCardContainerClassName?: string;
    isAuthenticated?: boolean;
    isInOddLayout?: boolean;
    onClickOnBoardingCard?: Function;
}): React.ReactElement[] => {
    const [card1, card2, card3, ...remainingCards] = cardsComponent;

    let cardsWithOnboarding = [
        card3,
        <OnboardingEntry
            type="standardPurple"
            containerClasses={eventCardContainerClassName}
            key="onboarding-entry"
            isAuthenticated={isAuthenticated}
            onButtonClick={onClickOnBoardingCard?.bind(null)}
        />,
    ];

    if (isInOddLayout) {
        cardsWithOnboarding = [
            <OnboardingEntry
                type="standardPurple"
                containerClasses={eventCardContainerClassName}
                key="onboarding-entry"
                isAuthenticated={isAuthenticated}
                onButtonClick={onClickOnBoardingCard?.bind(null)}
            />,
            card3,
        ];
    }

    const _cardsComponent = [
        card1,
        card2,
        ...cardsWithOnboarding,
        ...take(remainingCards, remainingCards.length),
    ];

    return _cardsComponent;
};

interface ConsumerCardsProps {
    bucketKey?: string;
    onViewEvent?: Function;
    fetchUserSavedEvents: Function;
    bucketLayoutConfig?: { [key: string]: any };
    cardType?: string;
    eventsGroups?: any[];
    isAuthenticated?: boolean;
    isFreeTab?: boolean;
    isInOddLayout?: boolean;
    onClickOnBoardingCard?: Function;
    shouldShowOnboardingCard?: boolean;
    isFirstBucket?: boolean;
    enableCategoryBrowse?: boolean;
}

interface Event extends FormattedEvent {
    hideStartDate?: boolean;
    numFollowers?: number;
    venueName?: string;
    venueAddress?: string;
}

const ConsumerCards = ({
    //TODO: update api to handle passing custom tracking labels
    bucketKey,
    onViewEvent,
    fetchUserSavedEvents,
    bucketLayoutConfig,
    cardType,
    eventsGroups,
    isAuthenticated,
    isFreeTab,
    isInOddLayout,
    onClickOnBoardingCard,
    shouldShowOnboardingCard,
    enableCategoryBrowse,
}: ConsumerCardsProps) => {
    const [hasFetchedSavedEvents, setHasFetchedSavedEvents] = React.useState(
        false,
    );
    const bucketClassNames =
        (bucketLayoutConfig && bucketLayoutConfig.bucketClassNames) || '';

    useEffect(() => {
        if (!hasFetchedSavedEvents) {
            setHasFetchedSavedEvents(true);
            fetchUserSavedEvents();
        }
    }, [fetchUserSavedEvents, hasFetchedSavedEvents]);

    let cardsComponent: React.ReactElement[] = reduce(
        eventsGroups,
        (
            memo: any,
            {
                events,
                containerClasses,
            }: {
                events: Event[];
                containerClasses: string;
            },
        ) => {
            const eventCardsComponents = events.map(
                ({
                    id,
                    minPrice,
                    isFree,
                    isSoldOut,
                    startDate,
                    hideStartDate,
                    numFollowers,
                    venueName,
                    venueAddress,
                    ...cardProps
                }) => {
                    //Once query client properly set up this can be removed
                    const signal = parseFollowSignal(numFollowers || 0);

                    return (
                        <div className={`${containerClasses}`} key={id}>
                            <EventCard
                                {...CONSUMER_CARD_DEFAULTS}
                                {...signal}
                                containerSpacingClasses=""
                                eventData={{
                                    ...cardProps,
                                    id: id || '',
                                    isFree: (!isFreeTab && isFree) || false,
                                    isSoldOut: isSoldOut,
                                    startDate: hideStartDate ? '' : startDate,
                                    organizerFollowerCount: numFollowers,
                                }}
                                onClick={onViewEvent?.bind(
                                    null,
                                    id,
                                    bucketKey,
                                    numFollowers,
                                )}
                                locationInfo={getLocationInfo(
                                    venueName,
                                    venueAddress,
                                )}
                                minPrice={minPrice}
                                gaCategory={GA_HOME_FEED_CATEGORY}
                                hidePriceDescription={isFreeTab}
                                isAuthenticated={isAuthenticated || false}
                                affCode="ebdssbcitybrowse"
                            />
                        </div>
                    );
                },
            );

            return [...memo, ...eventCardsComponents];
        },
        [],
    );

    /*
        shouldShowOnboardingCard: Property of bucket defined in tabConfig
    */
    if (shouldShowOnboardingCard) {
        const eventCardContainerClassName = classNames(
            'feed__card-cell',
            'eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1',
            {
                'eds-g-cell-sm-6-12 eds-g-cell-mn-4-12 eds-g-cell-lg-3-12':
                    cardType === GRID_TYPE,
            },
        );

        cardsComponent = _insertOnboardingCard({
            cardsComponent,
            eventCardContainerClassName,
            isAuthenticated,
            isInOddLayout,
            onClickOnBoardingCard,
        });
    }

    if (enableCategoryBrowse) {
        cardsComponent = _insertCategories({
            cards: cardsComponent,
            isInOddLayout,
        });
    }

    return <div className={bucketClassNames}>{cardsComponent}</div>;
};

export default ConsumerCards;
